import { createFileRoute, Link } from "@tanstack/react-router";
import { Code } from "../../components/Code";

export const Route = createFileRoute("/docs/tanstack-start")({
  component: TanStackGuide,
});

const ENV = `# publishable key — safe in the browser
VITE_NOTIFY_KEY=ntfy_pk_your_publishable_key
# secret key — server-side only, never VITE_
NOTIFY_SECRET=ntfy_sk_your_secret_key`;

const SW = `importScripts("https://api.getnotify.dev/sw.js");`;

const HEAD = `import { createRootRoute } from "@tanstack/react-router";

export const Route = createRootRoute({
  head: () => ({
    // add this alongside any existing meta / links
    scripts: [
      {
        src:
          "https://api.getnotify.dev/notify.js?token=" +
          import.meta.env.VITE_NOTIFY_KEY,
      },
    ],
  }),
  // ...your existing shellComponent
});`;

const BUTTON = `declare global {
  interface Window {
    notify?: { subscribe: (userId: string) => Promise<unknown> };
  }
}

export function EnableNotifications({ userId }: { userId: string }) {
  return (
    <button onClick={() => window.notify?.subscribe(userId).catch(console.error)}>
      Enable notifications
    </button>
  );
}`;

const SERVERFN = `import { createServerFn } from "@tanstack/react-start";

export const notifyUser = createServerFn({ method: "POST" })
  .validator((data: { userId: string; body: string }) => data)
  .handler(async ({ data }) => {
    const res = await fetch("https://api.getnotify.dev/send", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        token: process.env.NOTIFY_SECRET,
        userId: data.userId,
        title: "Acme",
        body: data.body,
      }),
    });
    if (!res.ok) throw new Error("notify: send failed " + res.status);
  });`;

const CALL = `await notifyUser({ data: { userId: user.id, body: "Your export is ready!" } });`;

function TanStackGuide() {
  return (
    <>
      <h1>TanStack Start</h1>
      <p className="lead">
        Add native browser push to a TanStack Start app — using the root route’s head for the script
        and a server function to send.
      </p>

      <h2>Prerequisites</h2>
      <p>
        Get your keys with <span className="icode">npx notify-dev init</span> and add them to{" "}
        <span className="icode">.env</span>:
      </p>
      <Code fname=".env" copyText={ENV}>
        {ENV}
      </Code>
      <div className="callout">
        Two keys: the <strong>publishable</strong> key (<span className="icode">VITE_</span>, inlined
        into the browser bundle) and the <strong>secret</strong> key, read at runtime in the server
        function via <span className="icode">process.env</span> — never expose it with{" "}
        <span className="icode">VITE_</span>.
      </div>

      <h2>1. Add the service worker</h2>
      <p>
        Create <span className="icode">public/notify-sw.js</span> (served at{" "}
        <span className="icode">/notify-sw.js</span>, same-origin with your pages):
      </p>
      <Code fname="public/notify-sw.js" copyText={SW}>
        {SW}
      </Code>

      <h2>2. Load notify.js</h2>
      <p>
        Inject the script from your root route’s <span className="icode">head()</span> — if you
        already return <span className="icode">meta</span>/<span className="icode">links</span>, just
        add the <span className="icode">scripts</span> array next to them.
      </p>
      <Code fname="src/routes/__root.tsx" copyText={HEAD}>
        {HEAD}
      </Code>

      <h2>3. Subscribe a user</h2>
      <p>A component that subscribes once the user opts in.</p>
      <Code fname="src/components/EnableNotifications.tsx" copyText={BUTTON}>
        {BUTTON}
      </Code>

      <h2>4. Send from a server function</h2>
      <p>
        A <span className="icode">createServerFn</span> keeps the request server-side; call it from a
        component, action, or loader.
      </p>
      <Code fname="src/functions/notify.ts" copyText={SERVERFN}>
        {SERVERFN}
      </Code>
      <Code badge="client or server" copyText={CALL}>
        {CALL}
      </Code>

      <div className="callout">
        <span className="icode">VITE_NOTIFY_KEY</span> (publishable) is inlined at build time, so set
        it before building — that’s fine, it’s meant for the browser. Set{" "}
        <span className="icode">NOTIFY_SECRET</span> as a server-side env var / Worker secret only.
      </div>

      <div className="doc-foot">
        <Link to="/docs/nextjs" className="ilink">
          ← Next.js
        </Link>
        <Link to="/docs/api-reference" className="ilink">
          API reference →
        </Link>
      </div>
    </>
  );
}
